import { ReactNode } from 'react'

import { CockpitScopeChip } from '../../../components/cockpitScopeChip'

import styles from './CockpitTileControls.module.scss'

/** Names the grouping selector, on screen and in an exported snapshot alike. */
export const COCKPIT_GROUP_BY_LABEL = 'Group by'

export interface ICockpitGroupOption<TValue extends string> {
  value: TValue
  label: string
}

interface ICockpitTileControlsProps<TValue extends string> {
  groupLabel?: string
  groupOptions: ICockpitGroupOption<TValue>[]
  selectedGroup: TValue
  onSelectGroup: (value: TValue) => void
  /** Controls rendered before the Group by selector. */
  children?: ReactNode
  /** Rendered at the far end of the row. */
  actions?: ReactNode
}

/** Control row above a sub-tab's chart: the Group by selector plus whatever else the sub-tab
 *  needs. */
export const CockpitTileControls = <TValue extends string>({
  groupLabel = COCKPIT_GROUP_BY_LABEL,
  groupOptions,
  selectedGroup,
  onSelectGroup,
  children,
  actions,
}: ICockpitTileControlsProps<TValue>) => {
  const selectedOption = groupOptions.find(({ value }) => value === selectedGroup)

  return (
    <div className={styles.root}>
      {children}

      <div className={styles.control}>
        <span className={styles.label}>{groupLabel}</span>
        <CockpitScopeChip
          appearance="pill"
          value={selectedOption?.label ?? selectedGroup}
          label={groupLabel}
          ariaLabel={groupLabel}
          options={groupOptions.map(({ value, label }) => ({ key: value, label }))}
          selectedKeys={[selectedGroup]}
          onSelect={(key) => onSelectGroup(key as TValue)}
          sizeValues={groupOptions.map(({ label }) => label)}
        />
      </div>

      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  )
}

CockpitTileControls.displayName = 'CockpitTileControls'
